import React, { useState } from 'react';
import styled from 'styled-components';
import { FaSignOutAlt, FaSync, FaWallet } from 'react-icons/fa';
import { BiCoin } from 'react-icons/bi';
import Button from '../ui/Button';
import ColorPicker from '../ColorPicker';


const HeaderContainer = styled.header`
  padding: ${props => props.theme.spacing.md} 0;
  background-color: ${props => props.theme.colors.primaryAlpha(0.8)};
  backdrop-filter: blur(8px);
  border-bottom: 1px solid ${props => props.theme.colors.border};
  position: sticky;
  top: 0;
  z-index: 100;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 0 ${props => props.theme.spacing.md};
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  position: relative;

  svg {
    margin-right: ${props => props.theme.spacing.sm};
    color: ${props => props.theme.colors.accent};
    transition: all ${props => props.theme.transitions.fast};
  }

  &:hover {
    transform: translateY(-2px);
    
    svg {
      color: ${props => props.theme.colors.primary};
      transform: rotate(10deg);
    }
  }

  &::after {
    content: '🎨';
    position: absolute;
    top: -5px;
    right: -5px;
    font-size: 12px;
    opacity: 0;
    transition: opacity ${props => props.theme.transitions.fast};
  }

  &:hover::after {
    opacity: 1;
  }
`;

const WalletInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-end;
  }
`;

const BalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const BalanceItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background-color: ${props => props.theme.colors.primaryAlpha(0.15)};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.sm};

  .icon {
    color: ${props => props.theme.colors.accent};
  }

  .amount {
    font-weight: 600;
  }
`;


const WalletAddress = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.muted};
  margin-right: ${props => props.theme.spacing.sm};
`;

const ConnectCta = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}, ${props => props.theme.colors.primary});
  color: white;
  border: none;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Header = ({ wallet, onDisconnect, onConnectClick, onRefreshWalletConnection, isRainbowKitReady = true }) => {
    const [showColorPicker, setShowColorPicker] = useState(false);

    const handleLogoClick = () => {
        setShowColorPicker(true);
    };

    const handleCloseColorPicker = () => {
        setShowColorPicker(false);
    };

    return (
        <>
            <HeaderContainer>
                <HeaderContent>
                    <Logo onClick={handleLogoClick} title="Click to customize theme colors">
                        <BiCoin size={30} />
                        <span>DEX DCA Bot</span>
                    </Logo>

                    {wallet ? (
                        <WalletInfo>
                            
                            <BalanceContainer>
                                <BalanceItem>
                                    <span className="icon">GTAI</span>
                                    <span className="amount">
                                        {/* Display GTAI balance, defaulting to 0 if not available */}
                                        {wallet.balance_gtai ? wallet.balance_gtai.toFixed(8) : "0.00000000"}
                                    </span>
                                </BalanceItem>
                                <BalanceItem>
                                    <span className="icon">USDT</span>
                                    <span className="amount">
                                        {/* Display USDT balance, defaulting to 0 if not available */}
                                        {wallet.balance_usdt ? wallet.balance_usdt.toFixed(8) : "0.00000000"}
                                    </span>
                                </BalanceItem>
                                <BalanceItem>
                                    <span className="icon">BNB</span>
                                    <span className="amount">
                                        {/* Display BNB balance for BSC network */}
                                        {(wallet.balance_bnb || 0).toFixed(8)}
                                    </span>
                                </BalanceItem>
                            </BalanceContainer>

                            <div>
                                <WalletAddress>
                                    {wallet.address.substring(0, 6)}...{wallet.address.substring(wallet.address.length - 4)}
                                </WalletAddress>
                                {onRefreshWalletConnection && (
                                    <Button
                                        size="small"
                                        variant="secondary"
                                        onClick={onRefreshWalletConnection}
                                        style={{ marginRight: '8px' }}
                                        title="Refresh wallet connection"
                                    >
                                        <FaSync size={12} />
                                    </Button>
                                )}
                                <Button
                                    size="small"
                                    variant="danger"
                                    onClick={onDisconnect}
                                >
                                    <FaSignOutAlt size={14} />
                                    Disconnect
                                </Button>
                            </div>
                        </WalletInfo>
                    ) : (
                        
                        <ConnectCta
                          onClick={onConnectClick}
                          disabled={!isRainbowKitReady}
                          title={isRainbowKitReady ? 'Connect Wallet' : 'Wallet connection loading...'}
                          aria-label="Connect Wallet"
                        >
                          <FaWallet size={24} />
                        </ConnectCta>
                    )}
                </HeaderContent>
            </HeaderContainer>

            <ColorPicker 
                show={showColorPicker} 
                onClose={handleCloseColorPicker} 
            />
        </>
    );
};

export default Header;